<!--
  @component BuildPanel — connect a repo + build from source.
  tier:      composed panel (consumer Deployments)
  reads:     useDeploymentIO → projects.get / projects.upsert (sourceRepoUrl)
                             + build.start / build.list / build.logs
  purpose:   the from-source half of deploy — point at a Gitea/GitHub repo,
             build an image (nixpacks or Dockerfile), watch it. release.run then
             deploys the built image with the project's env/secrets injected.
             Until the builder service is live, build.start returns an honest
             "builder not configured" error (never a fake image).
-->
<script lang="ts">
  import { useDeploymentIO } from "../io/nodes/deployment/DeploymentIO.svelte";

  interface Props { projectId: string; }
  const { projectId }: Props = $props();

  const node = useDeploymentIO();

  interface BuildRow { id: string; ref: string; status: string; imageRef: string | null; createdAtMs: number; }
  let sourceRepoUrl = $state<string>("");
  let repoDraft = $state<string>("");
  let ref = $state<string>("main");
  let builds = $state<readonly BuildRow[]>([]);
  let loading = $state(true);
  let saving = $state(false);
  let building = $state(false);
  let error = $state<string | null>(null);
  let openLogs = $state<{ id: string; text: string } | null>(null);

  const repoDirty = $derived(repoDraft.trim() !== (sourceRepoUrl ?? ""));

  async function load(): Promise<void> {
    if (!projectId) return;
    loading = true; error = null;
    try {
      const p = (await node.dispatch("projects.get", { id: projectId })) as { sourceRepoUrl?: string | null } | null;
      sourceRepoUrl = p?.sourceRepoUrl ?? "";
      repoDraft = sourceRepoUrl;
      const r = (await node.dispatch("build.list", { projectId })) as { builds?: BuildRow[] };
      builds = r?.builds ?? [];
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      loading = false;
    }
  }
  $effect(() => { void load(); });

  async function saveRepo(): Promise<void> {
    saving = true; error = null;
    try {
      await node.dispatch("projects.upsert", { id: projectId, sourceRepoUrl: repoDraft.trim() || null });
      sourceRepoUrl = repoDraft.trim();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { saving = false; }
  }

  async function startBuild(): Promise<void> {
    building = true; error = null;
    try {
      const r = (await node.dispatch("build.start", { projectId, ref: ref.trim() || "main" })) as { status?: string; error?: string };
      if (r?.error) error = r.error;
      await load();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { building = false; }
  }

  async function viewLogs(id: string): Promise<void> {
    try {
      const r = (await node.dispatch("build.logs", { buildId: id })) as { logs?: string };
      openLogs = { id, text: r?.logs || "(no logs)" };
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
  }

  function statusClass(s: string): string {
    if (s === "succeeded") return "ok";
    if (s === "failed") return "bad";
    return "warn"; // queued / building
  }
  function timeAgo(ms: number): string {
    const d = Date.now() - ms;
    if (d < 60_000) return "just now";
    if (d < 3_600_000) return `${Math.floor(d / 60_000)}m ago`;
    if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h ago`;
    return `${Math.floor(d / 86_400_000)}d ago`;
  }
</script>

<div class="build">
  {#if error}<p class="err">{error}</p>{/if}

  <!-- Source repo -->
  <div class="repo">
    <input type="text" placeholder="git.liquidefy.io/you/app  (repo to build)" bind:value={repoDraft} spellcheck="false" />
    <input type="text" class="ref" placeholder="branch" bind:value={ref} spellcheck="false" />
    <button type="button" class="save" disabled={saving || !repoDirty} onclick={() => void saveRepo()}>{saving ? "Saving…" : "Save"}</button>
  </div>

  <button type="button" class="build-btn" disabled={building || !sourceRepoUrl} onclick={() => void startBuild()}>
    {building ? "Building…" : "Build from source"}
  </button>
  {#if !sourceRepoUrl && !loading}<p class="hint">Set a repository above to build from source. With no repo, deploy a prebuilt image instead.</p>{/if}

  <!-- Build history -->
  {#if !loading && builds.length > 0}
    <ul class="rows">
      {#each builds as b (b.id)}
        <li class="row">
          <span class="pill {statusClass(b.status)}">{b.status}</span>
          <code class="ref-tag">{b.ref}</code>
          <span class="time">{timeAgo(b.createdAtMs)}</span>
          <button type="button" class="logs" onclick={() => void viewLogs(b.id)}>logs</button>
        </li>
      {/each}
    </ul>
  {:else if !loading}
    <p class="hint">No builds yet.</p>
  {/if}

  {#if openLogs}
    <div class="logbox">
      <div class="logbox-head"><span>Build logs</span><button type="button" onclick={() => (openLogs = null)}>✕</button></div>
      <pre>{openLogs.text}</pre>
    </div>
  {/if}
</div>

<style>
  .build { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--type-font); }
  .err { margin: 0; font-size: 12px; color: var(--error); }
  .hint { margin: 0; font-size: 12px; color: var(--text-tertiary); line-height: 1.5; }
  .repo { display: flex; gap: var(--space-2); }
  .repo input { flex: 1; min-width: 0; padding: 7px var(--space-3); background: var(--surface-sunken); border: 0.5px solid var(--border); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 12px; font-family: var(--type-mono); }
  .repo input.ref { flex: 0 0 110px; }
  .repo input:focus { outline: none; border-color: var(--border-focus); }
  .save { appearance: none; background: transparent; border: 0.5px solid var(--border); border-radius: var(--radius-md); color: var(--text-secondary); font: inherit; font-size: 12.5px; padding: 0 var(--space-3); cursor: pointer; white-space: nowrap; }
  .save:hover:not(:disabled) { background: var(--bg-hover); color: var(--text-primary); }
  .save:disabled { opacity: 0.45; cursor: default; }
  .build-btn { appearance: none; align-self: flex-start; background: var(--bg-active); border: 0.5px solid var(--border-strong); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 13px; font-weight: 500; padding: 8px var(--space-4); cursor: pointer; transition: background var(--ease-fast); }
  .build-btn:hover:not(:disabled) { background: var(--bg-selected); }
  .build-btn:disabled { opacity: 0.45; cursor: not-allowed; }

  .rows { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; }
  .row { display: grid; grid-template-columns: auto auto 1fr auto; gap: var(--space-3); align-items: center; padding: 7px 0; border-top: 0.5px solid var(--border-subtle); }
  .row:first-child { border-top: 0; }
  .pill { font-size: 10px; padding: 2px 8px; border-radius: var(--radius-pill); text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
  .pill.ok { background: var(--success-subtle); color: var(--success); }
  .pill.bad { background: var(--error-subtle); color: var(--error); }
  .pill.warn { background: var(--warning-subtle); color: var(--warning); }
  .ref-tag { font-family: var(--type-mono); font-size: 11px; color: var(--text-secondary); }
  .time { font-size: 12px; color: var(--text-ghost); }
  .logs { appearance: none; background: transparent; border: 0; color: var(--text-tertiary); font: inherit; font-size: 11.5px; cursor: pointer; }
  .logs:hover { color: var(--text-primary); }

  .logbox { border: 0.5px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .logbox-head { display: flex; justify-content: space-between; align-items: center; padding: 6px var(--space-3); background: var(--surface-sunken); font-size: 11.5px; color: var(--text-secondary); }
  .logbox-head button { appearance: none; background: transparent; border: 0; color: var(--text-ghost); cursor: pointer; font-size: 11px; }
  .logbox pre { margin: 0; padding: var(--space-3); max-height: 280px; overflow: auto; font-family: var(--type-mono); font-size: 11px; line-height: 1.5; color: var(--text-secondary); white-space: pre-wrap; word-break: break-word; }
</style>
